import { jwtDecode } from 'jwt-decode';
import Cookies from 'cookies-js';
import MAuth from '../models/MAuth';
import MFlags from '../models/MFlags';
import UserService from '../auth/UserService';

/**
 * Utility class for validating account_uuid in JWT tokens against organization context
 */
class TokenValidator {

  static isValidationEnabled() {
    return !!MFlags.redirectOrganizationSelectEnabled;
  }

  /**
   * Extract account_uuid from the access token
   * @returns {string|null} The account_uuid if present, null otherwise
   */
  static getAccountUuidFromToken() {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        return null;
      }

      const decoded = jwtDecode(accessToken);
      return decoded?.account_uuid || null;
    } catch (error) {
      console.error('Error decoding access token for account_uuid:', error);
      return null;
    }
  }

  /**
   * Validate that the account_uuid in the token matches the organization's account_uuid
   * @param {Object} organization - The organization object to validate
   * @returns {boolean} True if validation passes or no validation needed, false otherwise
   */
  static validateOrganizationAccess(organization) {
    if (!this.isValidationEnabled()) {
      return true;
    }

    if (!organization) {
      return true; // No organization to validate
    }

    const accountUuidFromToken = this.getAccountUuidFromToken();

    // If token doesn't have account_uuid, allow access (backward compatibility)
    if (!accountUuidFromToken) {
      return true;
    }

    const organizationAccountUuid = organization.accountUUID;

    // If organization doesn't have account_uuid, allow access (legacy data)
    if (!organizationAccountUuid) {
      return true;
    }

    // Compare account UUIDs
    const isValid = accountUuidFromToken === organizationAccountUuid;

    if (!isValid) {
      console.error(
        'Account UUID mismatch!',
        'Token account_uuid:', accountUuidFromToken,
        'Organization account_uuid:', organizationAccountUuid,
        'Organization ID:', organization.id
      );
    }

    return isValid;
  }

  /**
   * Validate that the account_uuid in the token matches the team's organization account_uuid
   * @param {Object} team - The team object to validate
   * @returns {boolean} True if validation passes or no validation needed, false otherwise
   */
  static validateTeamAccess(team) {
    if (!this.isValidationEnabled()) {
      return true;
    }

    if (!team) {
      return true; // No team to validate
    }

    const organization = team.organization || (team.organizationId && MAuth.getOrganization(team.organizationId));
    if (!organization) {
      return true; // Missing organization details to validate
    }

    return this.validateOrganizationAccess(organization);
  }

  /**
   * Validate that the account_uuid in the token matches the project's organization account_uuid
   * @param {Object} project - The project object to validate
   * @returns {boolean} True if validation passes or no validation needed, false otherwise
   */
  static validateProjectAccess(project) {
    if (!this.isValidationEnabled()) {
      return true;
    }

    if (!project || !project.team) {
      return true; // No project to validate
    }

    return this.validateTeamAccess(project.team);
  }

  /**
   * Validate organization access by ID
   * @param {number|string} organizationId - The organization ID to validate
   * @returns {boolean} True if validation passes or no validation needed, false otherwise
   */
  static validateOrganizationAccessById(organizationId) {
    if (!this.isValidationEnabled()) {
      return true;
    }

    if (!organizationId) {
      return true;
    }

    const organization = MAuth.getOrganization(organizationId);
    return this.validateOrganizationAccess(organization);
  }

  /**
   * Redirect to Keycloak login page when account_uuid mismatch is detected.
   * Note: This method does NOT clear cookies or sensitive state before redirecting.
   * @param {string} resourceType - Type of resource being accessed (organization/team/project)
   * @param {number|string} resourceId - ID of the resource
   */
  static handleAccessDenied({ resourceType, resourceId, organizationId }) {
    if (!this.isValidationEnabled()) {
      return;
    }

    const resolvedOrganizationId = organizationId ?? (resourceType === 'organization' ? resourceId : undefined);
    console.error(`Access denied to ${resourceType} ${resourceId} due to account_uuid mismatch - redirecting to Keycloak login`,
      resolvedOrganizationId ? `| organizationId: ${resolvedOrganizationId}` : '');

    // Redirect directly to Keycloak login with current URL as redirect parameter
    const redirectURL = window.location.pathname + window.location.search;
    if (resolvedOrganizationId) {
      UserService.login(redirectURL, { organizationId: resolvedOrganizationId });
    } else {
      UserService.login(redirectURL);
    }
  }

  static redirectToLogin(resourceType, resourceId, organizationId) {
    this.handleAccessDenied({ resourceType, resourceId, organizationId });
  }
}

export default TokenValidator;

